<div class="wrapper">
    <!-- Resolving which onboarding path applies (distributor vs self-service). Held so the self-service
         form never flashes before the distributor branch is known. Lockout is known synchronously. -->
    <ng-container *ngIf="resolving && !activation_lockout">
        <div class="resolving">
            <mat-spinner diameter="32"></mat-spinner>
            <p>Loading…</p>
        </div>
    </ng-container>

    <!-- Distributor-governed country: route the MSP to its distributor (no pricing table, no card). -->
    <ng-container *ngIf="!resolving && !activation_lockout && distributor">
        <div class="distributor-branch">
            <h3 class="font-bold">Your trial has ended.</h3>

            <p class="distributor-lead">
                Check your emails to start your Subscription - you should have received an email from
                <strong>{{ distributor.name }}</strong>, our distributor in <strong>{{ countryName }}</strong>.
            </p>

            <p class="distributor-support">
                If you haven't received an email from someone at
                <strong>{{ distributorDomain || distributor.name }}</strong>, or you are not based in
                {{ countryName }}, please contact
                <a href="mailto:support@octiga.com">support&#64;octiga.com</a>
            </p>

            <div class="confirm">
                <button mat-raised-button (click)="deleteSession()">Logout</button>
            </div>
        </div>
    </ng-container>

    <ng-container *ngIf="!resolving && !activation_lockout && !distributor">
        <!-- Two-step self-service signup: 1) pick a plan + see pricing, 2) billing + Stripe Elements -->
        <ng-container *ngIf="!success">
            <h3>Your trial has ended — choose a plan to continue</h3>

            <!-- Step indicator -->
            <ol class="step-indicator">
                <li [class.active]="step === 1" [class.done]="step > 1">1. Choose plan</li>
                <li [class.active]="step === 2">2. Billing details</li>
            </ol>

            <div class="content">
                <form class="signup-form" [formGroup]="signupForm" (ngSubmit)="submit()">
                    <!-- STEP 1: choose a plan; the pricing table for the resolved currency is shown below.
                         Kept in the DOM (hidden) on step 2 so navigating back doesn't re-run anything. -->
                    <div class="step" [class.step-hidden]="step !== 1">
                        <p class="intro">
                            Pick the plan that suits you best. You won't be charged for making a selection — we'll
                            confirm your billing details on the next step before anything is charged.
                        </p>

                        <div class="plan-cards" role="radiogroup" aria-label="Plan">
                            <button
                                type="button"
                                class="plan-card"
                                role="radio"
                                [attr.aria-checked]="signupForm.get('plan')?.value === 'FLEX'"
                                [class.selected]="signupForm.get('plan')?.value === 'FLEX'"
                                (click)="selectPlan('FLEX')"
                            >
                                <span class="plan-card-title">Flex <span class="plan-card-badge">Pay monthly</span></span>
                                <span class="plan-card-desc">Month-to-month billing. No commitment, cancel anytime.</span>
                            </button>

                            <button
                                type="button"
                                class="plan-card"
                                role="radio"
                                [attr.aria-checked]="signupForm.get('plan')?.value === 'COMMIT'"
                                [class.selected]="signupForm.get('plan')?.value === 'COMMIT'"
                                (click)="selectPlan('COMMIT')"
                            >
                                <span class="plan-card-title">Commit <span class="plan-card-badge">Best value</span></span>
                                <span class="plan-card-desc">
                                    A 12-month plan, billed monthly. A lower monthly rate in return for an annual
                                    commitment.
                                </span>
                            </button>
                        </div>

                        <div class="field" *ngIf="signupForm.get('plan')?.value === 'COMMIT'">
                            <label class="field-label" for="minCommit">Minimum monthly commit (seats)</label>
                            <input id="minCommit" type="number" class="oct-input" formControlName="minCommit" min="1" step="1">
                        </div>

                        <div class="pricing" *ngIf="signupForm.get('plan')?.value">
                            <div class="pricing-header">
                                <span>Mailboxes</span>
                                <span>Per mailbox / mo{{ pricingCurrency ? ' (' + pricingCurrency + ')' : '' }}</span>
                            </div>

                            <p class="pricing-note" *ngIf="pricingLoading">Loading pricing…</p>

                            <ng-container *ngIf="!pricingLoading">
                                <div class="pricing-row" *ngFor="let tier of pricingTiers">
                                    <span>{{ tierRange(tier) }}</span>
                                    <span>{{ tierPrice(tier) }}</span>
                                </div>

                                <p class="pricing-note" *ngIf="pricingTiers.length">{{ planNote() }}</p>
                                <p class="pricing-note" *ngIf="!pricingTiers.length && pricingError">
                                    Couldn't load pricing — contact
                                    <a href="mailto:sales@octiga.com">sales&#64;octiga.com</a>.
                                </p>
                                <p class="pricing-note" *ngIf="!pricingTiers.length && !pricingError && pricingCurrency">
                                    No online pricing for {{ pricingCurrency }} — contact
                                    <a href="mailto:sales@octiga.com">sales&#64;octiga.com</a>.
                                </p>
                                <p class="pricing-note" *ngIf="!pricingTiers.length && !pricingError && !pricingCurrency">
                                    Pricing will be shown for your billing country.
                                </p>
                            </ng-container>
                        </div>
                    </div>

                    <!-- STEP 2: our own fields (company / VAT) + native Stripe Elements. Country lives in
                         the Stripe address element. -->
                    <div class="step" [class.step-hidden]="step !== 2">
                        <p class="intro">
                            Enter your billing and payment details. Your card is saved securely by Stripe — you are not
                            charged now.
                        </p>

                        <!-- Native Stripe Elements: email, billing name + address, tax ID, card. Each element
                             mounts into its container once step 2 is revealed; until then a shimmering
                             skeleton holds the space so nothing pops in / shifts. -->
                        <div class="stripe-section">
                            <label class="field-label">Billing email</label>
                            <div class="stripe-field">
                                <div #emailEl class="stripe-element"></div>
                                <div class="stripe-skeleton" *ngIf="!elementsMounted" aria-hidden="true">
                                    <span class="skeleton-line full"></span>
                                </div>
                            </div>

                            <label class="field-label">Billing address</label>
                            <div class="stripe-field">
                                <div #addressEl class="stripe-element"></div>
                                <div class="stripe-skeleton" *ngIf="!elementsMounted" aria-hidden="true">
                                    <span class="skeleton-line full"></span>
                                    <span class="skeleton-line full"></span>
                                    <div class="skeleton-row">
                                        <span class="skeleton-line"></span>
                                        <span class="skeleton-line"></span>
                                    </div>
                                    <span class="skeleton-line half"></span>
                                </div>
                            </div>

                            <!-- Hidden (not removed — the Stripe element stays mounted here, just told to
                                 render nothing via `visibility: 'never'`) for the US, in favour of the
                                 custom field below. Never both at once. -->
                            <div [class.field-hidden]="isUsCountry">
                                <label class="field-label">VAT / Tax ID</label>
                                <div class="stripe-field">
                                    <div #taxIdEl class="stripe-element"></div>
                                    <div class="stripe-skeleton" *ngIf="!elementsMounted" aria-hidden="true">
                                        <span class="skeleton-line full"></span>
                                    </div>
                                </div>
                            </div>

                            <!-- Stripe's Tax ID Element is fully hidden for the US (see `setCountry`) — these
                                 two fields replace both its business-name and tax-ID inputs. Business name
                                 is optional; it falls back to the billing-contact name if left blank. -->
                            <ng-container *ngIf="isUsCountry">
                                <div class="field">
                                    <label class="field-label" for="usBusinessName">Business name (optional)</label>
                                    <input
                                        id="usBusinessName"
                                        type="text"
                                        class="oct-input"
                                        formControlName="usBusinessName"
                                    />
                                </div>

                                <div class="field">
                                    <label class="field-label" for="usTaxId">US Tax ID (EIN)</label>
                                    <input
                                        id="usTaxId"
                                        type="text"
                                        class="oct-input"
                                        formControlName="usTaxId"
                                        placeholder="XX-XXXXXXX"
                                    />
                                </div>
                            </ng-container>

                            <label class="field-label">Card details</label>
                            <div class="stripe-field">
                                <div #cardEl class="stripe-element"></div>
                                <div class="stripe-skeleton" *ngIf="!elementsMounted" aria-hidden="true">
                                    <span class="skeleton-line full"></span>
                                    <div class="skeleton-row">
                                        <span class="skeleton-line"></span>
                                        <span class="skeleton-line"></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p class="error" *ngIf="errorMessage">{{ errorMessage }}</p>
                </form>
            </div>

            <!-- Step 1 actions: Continue (right) / Logout. Step 2 actions: Subscribe (right) / Back.
                 .confirm is row-reverse, so the first button in the DOM sits on the right. -->
            <div class="confirm" *ngIf="step === 1">
                <button mat-raised-button color="primary" [disabled]="!canContinue" (click)="next()">Continue</button>
                <button mat-raised-button (click)="deleteSession()">Logout</button>
            </div>
            <div class="confirm" *ngIf="step === 2">
                <button mat-raised-button color="primary" [disabled]="!canSubmit" (click)="submit()">
                    {{ submitting ? 'Saving…' : 'Subscribe' }}
                </button>
                <button mat-raised-button [disabled]="submitting" (click)="back()">Back</button>
            </div>
        </ng-container>

        <!-- Signup succeeded -->
        <ng-container *ngIf="success">
            <h3>
                <mat-icon class="success-icon">check_circle_outline</mat-icon>
                You're all set
            </h3>
            <div class="content">
                <p>
                    Thanks for subscribing. Your billing details are saved and your account is active. If you have any
                    questions, contact <a href="mailto:support@octiga.com">support&#64;octiga.com</a>.
                </p>
            </div>
            <div class="confirm">
                <button mat-raised-button color="primary" (click)="continue()">Continue</button>
            </div>
        </ng-container>
    </ng-container>

    <ng-container *ngIf="activation_lockout">
        <div class="mat-typology">
            <h3 class="font-bold">
                <mat-icon>warning_outlined</mat-icon>
                MSP Validation Required
            </h3>

            <p mat-dialog-content>
                The Octiga Team needs to validate your MSP/Service Provider before we can allow access to the Octiga
                portal.
            </p>

            <h3 style="margin-bottom: 1rem;" class="font-bold" mat-dialog-content>
                Please

                <a href="mailto:support@octiga.com?subject=Account%20Validation">Email Octiga support</a>
                 from your business email address with the following information and we will activate your account.
            </h3>

            <ul>
                <li class="font-bold m-0"> 1- Your Website:</li>
                <li class="font-bold m-0"> 2- Contact Email Address (If different to sending address):</li>
                <li class="font-bold m-0"> 3- Phone Number (Optional):</li>
            </ul>

            <p style="margin-top: 1rem;">Thank you</p>
            <p>The Octiga Team</p>

            <div class="confirm">
                <button mat-raised-button (click)="deleteSession()">Logout</button>
            </div>
        </div>
    </ng-container>
</div>
